import { useState, useEffect, useContext, useRef } from "react";
import LocContext from "../context/locContext";
import axios from "axios";
import ClientNavbar from "../layout/ClientNavbar";
import Swal from "sweetalert2";
import { NavLink } from "react-router-dom";

export default function Profile() {
  const { userData, refreshUserData } = useContext(LocContext);
  const id = userData?.id;

  const [userDataP, setUserDataP] = useState({});
  const fileInputRef = useRef(null);

  // ---------------- VALIDATION HELPERS ----------------
  const textOnlyRegex = /^[A-Za-z\s]+$/;
  const numberOnlyRegex = /^[0-9]+$/;
  const ifscRegex = /^[A-Z]{4}0[0-9]{6}$/;

  // Indian States (can expand)
  const INDIAN_STATES = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Tamil Nadu",
    "Telangana",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  // Cities example (replace with API later if needed)
  const CITIES = [
    "Ahmedabad",
    "Surat",
    "Vadodara",
    "Rajkot",
    "Mumbai",
    "Delhi",
    "Pune",
    "Bangalore",
    "Chennai",
  ];

  // Age validation
  const is18Plus = (dob) => {
    if (!dob) return false;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const [errors, setErrors] = useState({});
  const investorOnboardingComplete = Number(userData?.mutual_status) === 1;

  useEffect(() => {
    if (refreshUserData) refreshUserData();
  }, [refreshUserData]);

  const getProfileData = async () => {
    try {
      const [resUser, resCategory] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/${id}`),
        axios.get(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/investment/user/${id}`,
        ),
      ]);

      if (resUser.status === 200 && resCategory.status === 200) {
        setUserDataP({
          ...resUser.data.data, // personal details
          ...resCategory.data.data, // KYC / FATCA
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  const handleimgFe = (e) => {
    e.target.src = "/assets/img/user.png";
    e.target.alt = "Image not found";
  };

  // ---------- Edit Profile Photo ----------
  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/profile-photo/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (res.status === 200) {
        setUserDataP((prev) => ({ ...prev, photo: res.data.photo }));
      }
    } catch (err) {
      console.log("Image upload failed", err);
    }
  };

  // helper: update filled classes for all inputs (treat "-" as empty)
  const updateFilledClasses = () => {
    document.querySelectorAll(".form-control").forEach((el) => {
      const val = (el.value || "").toString().trim();
      if (val !== "" && val !== "-") {
        el.classList.add("filled");
      } else {
        el.classList.remove("filled");
      }
    });
  };

  // run whenever userDataP changes (after loading profile), so inputs get correct class initially
  useEffect(() => {
    // timeout ensures DOM updated
    setTimeout(updateFilledClasses, 0);
  }, [userDataP]);

  // ----------------- handleInputChange (replace your existing one) -----------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // TEXT ONLY FIELDS
    const textFields = [
      "first_name",
      "last_name",
      "bank_name",
      "branch_name",
      "nominee_name",
      "nominee_relationship",
      "guardian_details",
    ];

    if (textFields.includes(name)) {
      if (value && !textOnlyRegex.test(value)) return;
    }

    // NUMBER ONLY FIELDS
    const numberFields = ["account_number", "pin_code"];
    if (numberFields.includes(name)) {
      if (value && !numberOnlyRegex.test(value)) return;
    }

    setUserDataP((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // NEW: sanitize phone input so only digits are stored
  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");

    if (digits.length > 10) return;

    setUserDataP((prev) => ({
      ...prev,
      phone: digits,
    }));

    if (digits.length < 10) {
      setErrors((prev) => ({
        ...prev,
        phone: "Mobile number must be 10 digits",
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  /* ================= SUBMIT ================= */
  const headleSubmit = async (e) => {
    e.preventDefault();

    if (!investorOnboardingComplete) {
      Swal.fire({
        icon: "info",
        title: "Profile completion pending",
        html: "Please complete <strong>mutual fund investor onboarding</strong> first. After the exchange accepts your registration, you can save changes here.",
        confirmButtonText: "Go to onboarding",
        showCancelButton: true,
        cancelButtonText: "Close",
      }).then((r) => {
        if (r.isConfirmed) window.location.href = "/investor-onboarding";
      });
      return;
    }

    /* 🔴 BLOCK SUBMIT IF ERRORS */
    if (errors.date_of_birth || errors.ifsc_code || errors.phone) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Data",
        text: "Please fix validation errors before submitting",
      });
      return;
    }

    if (!userDataP.first_name?.trim()) {
      Swal.fire({
        icon: "warning",
        title: "First name is required",
      });
      return;
    }

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/updateUser/${id}`,
        userDataP,
      );

      if (res.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Profile Updated Successfully",
          timer: 1500,
        }).then(getProfileData);
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Update failed" });
    }
  };

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        /* Heading/link styles */
        h3 a:hover { text-decoration: underline; }

        /* Card headings */
        .card h3 {
          font-size: 24px; 
          color: #003366;
          margin-bottom: 10px;
          font-weight: 600; 
        }

        /* Change Dashboard NavLink color and hover */
        .profile-breadcrumb a {
          color: #1bafb4; /* custom color for breadcrumb link */
          font-weight: 600;
          text-decoration: none;
        }
        .profile-breadcrumb a:hover {
          color: #0f8c86;
          text-decoration: underline;
        }

        /* Transparent inputs and placeholder styling */
        .form-control {
          background: transparent !important;
          box-shadow: none !important;
        }
        .form-control::placeholder {
          color: rgba(0,0,0,0.45);
          font-weight: 400;
        }

        /* Filled inputs: bold black text */
        .form-control.filled {
          color: #000 !important;
          font-weight: 700 !important;
        }

        /* Disabled inputs: slightly muted but readable */
        .form-control:disabled {
          color: rgba(0,0,0,0.65) !important;
          font-weight: 600 !important;
          background: transparent !important;
          opacity: 1 !important;
        }

        /* smaller fixed styled country code input */
        .country-code {
          display: inline-block;
          width: 96px;
          min-width: 72px;
          padding: .375rem .5rem;
          border-radius: .375rem;
          background: rgba(255,255,255,0.06);
          color: #fff;
          border: 1px solid rgba(0,0,0,0.05);
          text-align: left;
          vertical-align: middle;
        }

        /* ensure phone input sits nicely next to country code */
        .phone-wrapper { display:flex; gap:8px; align-items:center; }

        /* Error border */
        .error-input { border-color: #dc2626 !important; }
      `}</style>

          <div className="card shadow-sm p-4 mb-4">
            <h3 className="text-primary mb-2">Profile Information</h3>

            {/* Breadcrumb with custom link color */}
            <nav
              aria-label="breadcrumb"
              style={{ "--bs-breadcrumb-divider": "none" }}
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/overview">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Profile Information
                </li>
              </ol>
            </nav>
            </div>

            {!investorOnboardingComplete && (
              <div className="card shadow-sm p-4 mb-4 border-0" style={{ borderRadius: "12px", background: "#fffbeb", borderLeft: "4px solid #f59e0b" }}>
                <h5 className="mb-2" style={{ color: "#92400e", fontWeight: "700" }}>
                  Profile completion pending
                </h5>
                <p className="mb-3" style={{ color: "#78350f", fontSize: "15px" }}>
                  Your profile details cannot be saved until you complete mutual fund investor onboarding with the exchange.
                  This is a one-time registration — you will receive updates by email while authentication is pending.
                </p>
                <NavLink to="/investor-onboarding" className="btn btn-warning fw-semibold">
                  Complete investor onboarding
                </NavLink>
              </div>
            )}

            <div className="card shadow-sm p-4 mb-4">

            <div className="col-md-2 col-12">
              <div
                className="card-header card-header-profile"
                style={{
                  position: "relative",
                  textAlign: "center",
                  gap: "10px",
                }}
              >
                <img
                  src={
                    userDataP?.photo
                      ? `${process.env.REACT_APP_BACKEND_BASE_URL_FILES}${userDataP?.photo}`
                      : "/assets/img/user.png"
                  }
                  draggable={false}
                  style={{
                    borderRadius: "12px",
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                  onError={handleimgFe}
                  alt="Profile"
                />

                {/* Edit/Delete buttons */}
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  <button
                    className="btn btn-sm btn-primary d-flex align-items-center"
                    onClick={handleEditClick}
                  >
                    <i
                      className="fas fa-edit"
                      style={{ marginRight: "6px" }}
                    ></i>
                    Edit
                  </button>
                </div>

                {/* Hidden file input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <form
              onSubmit={headleSubmit}
              style={{
                pointerEvents: investorOnboardingComplete ? "auto" : "none",
                opacity: investorOnboardingComplete ? 1 : 0.55,
              }}
            >
              {/* -------- Personal Details -------- */}
              <div className="row g-3 mt-3">
                <div className="card-header">
                  <div className="header-left">
                    <h3>Personal Details</h3>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>First Name</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.first_name || ""}
                    placeholder="Enter first name"
                    className="form-control"
                    name="first_name"
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Last Name</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.last_name || ""}
                    placeholder="Enter last name"
                    className="form-control"
                    name="last_name"
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Email Address</strong>
                  </label>
                  <input
                    type="email"
                    value={userDataP.email || ""}
                    placeholder="Enter your email"
                    className="form-control"
                    disabled={true}
                    readOnly
                  />
                </div>

                {/* PHONE: split into readonly country code + editable phone number */}
                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Phone</strong>
                  </label>

                  <div className="phone-wrapper">
                    {/* Country code (readonly) */}
                    <input
                      type="text"
                      value={userDataP.country_code || "+ 91"}
                      className="country-code form-control"
                      readOnly
                      aria-label="Country code"
                      disabled={true}
                    />

                    {/* Phone number (editable only digits) */}
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={userDataP.phone || ""}
                      placeholder="Enter mobile number"
                      className="form-control"
                      name="phone"
                      maxLength={15}
                      disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                      onChange={handlePhoneChange}
                      aria-label="Mobile number"
                      readOnly
                      // disabled={true}
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Date of Birth</strong>
                  </label>

                  <input
                    type="date"
                    value={userDataP.date_of_birth?.split("T")[0] || ""}
                    className={`form-control ${
                      errors.date_of_birth ? "error-input" : ""
                    }`}
                    name="date_of_birth"
                    onChange={(e) => {
                      const val = e.target.value;

                      setUserDataP((prev) => ({
                        ...prev,
                        date_of_birth: val,
                      }));

                      if (!is18Plus(val)) {
                        setErrors((prev) => ({
                          ...prev,
                          date_of_birth: "Age must be 18 years or above",
                        }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          date_of_birth: "",
                        }));
                      }
                    }}
                  />

                  {errors.date_of_birth && (
                    <small className="text-danger">
                      {errors.date_of_birth}
                    </small>
                  )}
                </div>

                {/* GENDER: dropdown as requested */}
                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Gender</strong>
                  </label>
                  <select
                    name="gender"
                    value={userDataP.gender || ""}
                    className="form-control"
                    onChange={handleInputChange}
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    aria-label="Gender"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* PAN: disabled edit (read-only) */}
                <div className="col-md-4">
                  <label className="form-label">
                    <strong>PAN Number</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.pan_number || ""}
                    placeholder="PAN Number"
                    className="form-control"
                    name="pan_number"
                    disabled={true} /* always disabled as requested */
                    readOnly
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Address Line 1</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.address_line1 || ""}
                    placeholder="Enter your address 1"
                    className="form-control"
                    name="address_line1"
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Address Line 2</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.address_line2 || ""}
                    placeholder="Enter your address 2"
                    className="form-control"
                    name="address_line2"
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Country</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.country || ""}
                    placeholder="Enter your country"
                    className="form-control"
                    name="country"
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>State</strong>
                  </label>
                  <input
                    list="stateList"
                    name="state"
                    value={userDataP.state || ""}
                    className="form-control"
                    placeholder="Select state"
                    onChange={handleInputChange}
                  />

                  <datalist id="stateList">
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    <strong>City</strong>
                  </label>

                  <input
                    type="text"
                    list="cityList"
                    name="city"
                    value={userDataP.city || ""}
                    placeholder="Select or type city"
                    className="form-control"
                    onChange={handleInputChange}
                  />

                  <datalist id="cityList">
                    {CITIES.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Pin Code</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.pin_code || ""}
                    placeholder="Enter your pin code"
                    className="form-control"
                    name="pin_code"
                    disabled={userDataP.ucc && userDataP.ucc !== "Reject"}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Residential Status</strong>
                  </label>
                  <select
                    name="residential_status"
                    value={userDataP.residential_status || ""}
                    className="form-control"
                    onChange={handleInputChange}
                  >
                    <option value="">Select</option>
                    <option value="Resident">Resident</option>
                    <option value="Non-Resident">Non-Resident</option>
                  </select>
                </div>
                {userDataP.ucc && userDataP.ucc !== "Reject" && (
                  <div className="col-md-4">
                    <label className="form-label">
                      <strong>UCC</strong>
                    </label>
                    <input
                      type="text"
                      value={userDataP.ucc}
                      placeholder="Enter your residential status"
                      className="form-control"
                      name="residential_status"
                      disabled={true}
                      onChange={handleInputChange}
                    />
                  </div>
                )}
              </div>

              {/* -------- Bank Details -------- */}
              <div className="row g-3 mt-3">
                <div className="card-header">
                  <div className="header-left">
                    <h3>Bank Details</h3>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Bank Name</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.bank_name || ""}
                    placeholder="Enter bank name"
                    className="form-control"
                    name="bank_name"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Branch Name</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.branch_name || ""}
                    placeholder="Enter branch name"
                    className="form-control"
                    name="branch_name"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Account Number</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.account_number || ""}
                    placeholder="Enter account number"
                    className="form-control"
                    name="account_number"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Account Type</strong>
                  </label>
                  <select
                    name="account_type"
                    value={userDataP.account_type || ""}
                    className="form-control"
                    onChange={handleInputChange}
                  >
                    <option value="">Select</option>
                    <option value="Savings">Savings</option>
                    <option value="Current">Current</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>IFSC Code</strong>
                  </label>

                  <input
                    type="text"
                    value={userDataP.ifsc_code || ""}
                    placeholder="e.g. SBIN0001234"
                    className={`form-control ${
                      errors.ifsc_code ? "error-input" : ""
                    }`}
                    name="ifsc_code"
                    maxLength={11}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();

                      setUserDataP((prev) => ({
                        ...prev,
                        ifsc_code: val,
                      }));

                      // validate only if something is typed
                      if (val && !ifscRegex.test(val)) {
                        setErrors((prev) => ({
                          ...prev,
                          ifsc_code: "Invalid IFSC code ",
                        }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          ifsc_code: "",
                        }));
                      }
                    }}
                  />

                  {errors.ifsc_code && (
                    <small className="text-danger">{errors.ifsc_code}</small>
                  )}
                </div>

                {/* <div className="col-md-4">
                  <label className="form-label">
                    <strong>IFSC Code</strong>
                  </label>

                  <input
                    type="text"
                    value={userDataP.ifsc_code || ""}
                    placeholder="Enter IFSC code"
                    className={`form-control ${
                      errors.ifsc_code ? "error-input" : ""
                    }`}
                    name="ifsc_code"
                    maxLength={11}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();

                      setUserDataP((prev) => ({
                        ...prev,
                        ifsc_code: val,
                      }));

                      // ✅ Only required validation
                      if (!val) {
                        setErrors((prev) => ({
                          ...prev,
                          ifsc_code: "IFSC code is required",
                        }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          ifsc_code: "",
                        }));
                      }
                    }}
                    onBlur={() => {
                      if (!userDataP.ifsc_code) {
                        setErrors((prev) => ({
                          ...prev,
                          ifsc_code: "IFSC code is required",
                        }));
                      }
                    }}
                  />

                  {errors.ifsc_code && (
                    <small className="text-danger">{errors.ifsc_code}</small>
                  )}
                </div> */}

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Nominee Name</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.nominee_name || ""}
                    placeholder="Enter nominee name"
                    className="form-control"
                    name="nominee_name"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Nominee Relationship</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.nominee_relationship || ""}
                    placeholder="Enter nominee relationship"
                    className="form-control"
                    name="nominee_relationship"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Nominee Date of Birth</strong>
                  </label>
                  <input
                    type="date"
                    value={
                      userDataP.nominee_dob
                        ? userDataP.nominee_dob.split("T")[0]
                        : ""
                    }
                    className="form-control"
                    name="nominee_dob"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    <strong>Guardian Details</strong>
                  </label>
                  <input
                    type="text"
                    value={userDataP.guardian_details || ""}
                    placeholder="Enter guardian details"
                    className="form-control"
                    name="guardian_details"
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mt-4">
                  <button type="submit" className="btn btn-primary px-4">
                    Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
    </>
  );
}

